import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Progress } from "antd";
import styled from "styled-components";
import {
  User as UserIcon,
  Camera,
  Star,
  Logout,
  ArrowRight2,
  Location,
  Calendar,
  Book,
  SearchNormal1,
  VideoPlay,
  Heart,
  Cup,
  Sms,
  Location as MapIcon,
  Folder,
  MessageQuestion,
  Setting2,
} from "iconsax-react";
import { useAuth } from "../../hooks/auth";
import { useGamification } from "../../hooks/useGamification";
import {
  getProfileDisplayName,
  getProfilePhotoSrc,
} from "../../utils/profilePhoto";
import MobileLayout from "../../components/MobileLayout";
import { SettingsMenuItem } from "../../shared/components/SettingsMenuItem";
import {
  weeklyLeaguesService,
  WeeklyLeagueEntry,
  LEAGUE_NAMES,
} from "../../services/weeklyLeaguesService";
import { favoritesService } from "../../services/favoritesService";
import {
  Main,
  UserCard,
  FeatureCard,
  UsageCard,
  UsageItem,
  SectionHeader,
  SectionTitle,
  SectionHint,
  Tag,
  GroupCard,
  ActionListItem,
  AchievementsGrid,
  AchievementCard,
} from "./styles";

const LIBRAS_LABEL: Record<string, string> = {
  basico: "Básico",
  intermediario: "Intermediário",
  avancado: "Avançado",
};

const InfoCard = styled.div`
  background: color-mix(in srgb, var(--color-info) 12%, var(--color-surface));
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 16px 20px;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

const UserProfile: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { getLevelProgress, getAllAchievementsWithStatus } = useGamification();

  const [leagueInfo, setLeagueInfo] = useState<WeeklyLeagueEntry | null>(null);
  const [favoritesCount, setFavoritesCount] = useState(
    () => user?.totalFavorites || 0
  );
  const [videosCount, setVideosCount] = useState(() => user?.videoCount || 0);

  const loadLeagueData = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const info = await weeklyLeaguesService.getUserLeagueInfo(user.uid);
      setLeagueInfo(info);
    } catch (error) {
      console.error("Failed to load league data:", error);
    }
  }, [user?.uid]);

  const loadUsageCounts = useCallback(async () => {
    if (!user?.uid) {
      setFavoritesCount(0);
      setVideosCount(0);
      return;
    }
    try {
      const [favCount, videos] = await Promise.all([
        favoritesService.getFavoritesCount(user.uid),
        favoritesService.getUserVideos(user.uid),
      ]);
      setFavoritesCount(favCount);
      setVideosCount(videos.length || user.videoCount || 0);
    } catch (error) {
      console.error("Failed to load usage counts:", error);
      setFavoritesCount(user.totalFavorites || 0);
      setVideosCount(user.videoCount || 0);
    }
  }, [user?.uid, user?.totalFavorites, user?.videoCount]);

  useEffect(() => {
    if (user?.uid) {
      loadLeagueData();
      loadUsageCounts();
    }
  }, [user?.uid, loadLeagueData, loadUsageCounts]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Failed to sign out:", error);
    }
  };

  const {
    points: currentPoints,
    currentLevel,
    pointsToNext,
    progress: levelProgress,
  } = getLevelProgress();

  const achievementsWithStatus = getAllAchievementsWithStatus();
  const unlockedCount = achievementsWithStatus.filter((a) => a.unlocked).length;

  const userLocation = [user?.city, user?.state, user?.country]
    .filter(Boolean)
    .join(", ");

  const avatarSrc = user ? getProfilePhotoSrc(user) : undefined;
  const displayName = getProfileDisplayName(user);
  const librasLabel = user?.librasFluencyLevel
    ? LIBRAS_LABEL[user.librasFluencyLevel.toLowerCase()] ||
      user.librasFluencyLevel
    : null;
  const leagueDisplayName = leagueInfo
    ? LEAGUE_NAMES[leagueInfo.leagueLevel] || `Liga ${leagueInfo.leagueLevel}`
    : null;

  return (
    <MobileLayout
      headerTitle="Perfil"
      headerType="private"
      headerSettingsHref="/settings"
    >
      <Main style={{ maxWidth: 600 }}>
        <SectionHeader>
          <SectionTitle>Seu perfil</SectionTitle>
          <SectionHint>
            Nome, foto, localização e papel na comunidade
          </SectionHint>
        </SectionHeader>

        <UserCard>
          <div className="avatar-wrapper">
            <Avatar
              size={112}
              src={avatarSrc}
              icon={
                !avatarSrc ? <UserIcon size={48} variant="Bold" /> : undefined
              }
              style={{
                border: "4px solid var(--color-surface)",
                boxShadow: "0 4px 20px rgba(95, 51, 225, 0.25)",
              }}
            />
            <button
              type="button"
              className="edit-btn"
              onClick={() => navigate("/edit-account")}
              aria-label="Editar foto de perfil"
            >
              <Camera size={16} variant="Bold" />
            </button>
          </div>
          <h2>{displayName}</h2>
          {user?.email ? (
            <div className="email-row">
              <Sms size={16} variant="Bold" />
              <span>{user.email}</span>
            </div>
          ) : null}
          {userLocation && (
            <div className="location">
              <Location size={16} variant="Bold" />
              <span>{userLocation}</span>
            </div>
          )}

          <div className="tag-row">
            <Tag>
              <UserIcon size={16} variant="Bold" />
              <span>{user?.isDeaf ? "Surdo" : "Ouvinte"}</span>
            </Tag>
            {user?.age && (
              <Tag>
                <Calendar size={16} variant="Bold" />
                <span>{user.age} anos</span>
              </Tag>
            )}
            <Tag>
              <Star size={16} variant="Bold" />
              <span>Nível {currentLevel}</span>
            </Tag>
            {librasLabel && (
              <Tag>
                <MessageQuestion size={16} variant="Bold" />
                <span>Libras: {librasLabel}</span>
              </Tag>
            )}
          </div>
        </UserCard>

        <SectionHeader>
          <SectionTitle>Meu progresso</SectionTitle>
          <SectionHint>
            Acompanhe seus pontos e evolução no Bilíngua.
          </SectionHint>
        </SectionHeader>

        <FeatureCard>
          <div className="card-header">
            <div className="badge">Meu Progresso</div>
            <div className="trophy-box">
              <Cup size={24} variant="Bold" />
            </div>
          </div>
          <div className="points-display">
            <h4>{currentPoints} Pontos</h4>
            <p>Continue aprendendo para subir de nível!</p>
          </div>

          <Progress
            percent={Math.round(levelProgress)}
            showInfo={false}
            strokeColor="#fff"
            trailColor="rgba(255, 255, 255, 0.2)"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "0.8rem",
              fontSize: "14px",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            <span>Nível {currentLevel}</span>
            <span>
              {pointsToNext > 0 ? `Faltam ${pointsToNext} XP` : "Nível máximo"}
            </span>
          </div>
        </FeatureCard>

        <SectionHeader>
          <SectionTitle>Conquistas</SectionTitle>
          <SectionHint>
            {unlockedCount} de {achievementsWithStatus.length} desbloqueadas
          </SectionHint>
        </SectionHeader>

        <AchievementsGrid>
          {achievementsWithStatus.map((achievement) => (
            <AchievementCard
              key={achievement.id}
              $unlocked={achievement.unlocked}
            >
              <div className="icon-wrapper" aria-hidden>
                {achievement.icon}
              </div>
              <h4>{achievement.name}</h4>
              <p>{achievement.description}</p>
            </AchievementCard>
          ))}
        </AchievementsGrid>

        {leagueInfo && (
          <FeatureCard $variant="success">
            <div className="card-header">
              <div className="badge">Sua Liga</div>
              <div className="trophy-box">
                <Star size={24} variant="Bold" />
              </div>
            </div>
            <div className="points-display">
              <h4>{leagueDisplayName}</h4>
              <p>Você está competindo com outros alunos!</p>
            </div>

            <div className="stats-grid">
              <div className="stat-box">
                <span className="val">{leagueInfo.xp}</span>
                <span className="lab">XP Semanal</span>
              </div>
              {leagueInfo.position && (
                <div className="stat-box">
                  <span className="val">{leagueInfo.position}º</span>
                  <span className="lab">Posição</span>
                </div>
              )}
            </div>

            <button
              type="button"
              className="view-more-btn"
              onClick={() => navigate("/ranking")}
            >
              Ver ranking completo
              <ArrowRight2 size={16} variant="Bold" />
            </button>
          </FeatureCard>
        )}

        <SectionHeader>
          <SectionTitle>Em números</SectionTitle>
          <SectionHint>Resumo do seu uso na plataforma.</SectionHint>
        </SectionHeader>

        <UsageCard>
          <div className="usage-list">
            <UsageItem>
              <div className="item-left">
                <div
                  className="icon-wrapper"
                  style={{
                    background: "color-mix(in srgb, #f97316 15%, transparent)",
                    color: "#f97316",
                  }}
                >
                  <Star size={20} variant="Bold" />
                </div>
                <span>Dias seguidos</span>
              </div>
              <span className="item-value">{user?.streakDays || 0} dias</span>
            </UsageItem>

            <UsageItem>
              <div className="item-left">
                <div
                  className="icon-wrapper"
                  style={{
                    background: "color-mix(in srgb, #16a34a 15%, transparent)",
                    color: "#16a34a",
                  }}
                >
                  <Book size={20} variant="Bold" />
                </div>
                <span>Palavras vistas</span>
              </div>
              <span className="item-value">{user?.totalWordsViewed || 0}</span>
            </UsageItem>

            <UsageItem>
              <div className="item-left">
                <div
                  className="icon-wrapper"
                  style={{
                    background: "color-mix(in srgb, #3b82f6 15%, transparent)",
                    color: "#3b82f6",
                  }}
                >
                  <SearchNormal1 size={20} variant="Bold" />
                </div>
                <span>Buscas realizadas</span>
              </div>
              <span className="item-value">{user?.totalSearches || 0}</span>
            </UsageItem>

            <UsageItem>
              <div className="item-left">
                <div
                  className="icon-wrapper"
                  style={{
                    background: "color-mix(in srgb, #8b5cf6 15%, transparent)",
                    color: "#8b5cf6",
                  }}
                >
                  <VideoPlay size={20} variant="Bold" />
                </div>
                <span>Vídeos enviados</span>
              </div>
              <span className="item-value">{videosCount}</span>
            </UsageItem>

            <UsageItem>
              <div className="item-left">
                <div
                  className="icon-wrapper"
                  style={{
                    background: "color-mix(in srgb, #f43f5e 15%, transparent)",
                    color: "#f43f5e",
                  }}
                >
                  <Heart size={20} variant="Bold" />
                </div>
                <span>Favoritos</span>
              </div>
              <span className="item-value">{favoritesCount}</span>
            </UsageItem>
          </div>
        </UsageCard>

        <SectionHeader>
          <SectionTitle>Aprendizado</SectionTitle>
          <SectionHint>Atalhos para as áreas principais do app.</SectionHint>
        </SectionHeader>

        <GroupCard>
          <SettingsMenuItem
            title="Mapa"
            description="Explore locais e conteúdos no mapa"
            icon={<MapIcon size={22} variant="Bold" />}
            onClick={() => navigate("/map")}
          />
          <SettingsMenuItem
            title="Ranking"
            description="Veja sua posição na liga semanal"
            icon={<Cup size={22} variant="Bold" />}
            onClick={() => navigate("/ranking")}
          />
          <SettingsMenuItem
            title="Favoritos"
            description="Palavras e conteúdos salvos"
            icon={<Heart size={22} variant="Bold" />}
            onClick={() => navigate("/favorites")}
          />
          <SettingsMenuItem
            title="Repositório pessoal"
            description="Seus vídeos e sinais gravados"
            icon={<Folder size={22} variant="Bold" />}
            onClick={() => navigate("/repository")}
          />
        </GroupCard>

        <InfoCard>
          Bilíngua prioriza acessibilidade visual e linguística. Use alto
          contraste e tamanhos confortáveis em Configurações → Personalização.
        </InfoCard>

        <GroupCard>
          <ActionListItem onClick={() => navigate("/settings")}>
            <div className="icon-box">
              <Setting2 size={20} variant="Bold" />
            </div>
            <div className="text-box">
              <h5>Configurações</h5>
              <span>Conta, tema, privacidade e suporte</span>
            </div>
            <ArrowRight2 size={18} className="arrow-icon" />
          </ActionListItem>

          <ActionListItem $danger onClick={handleLogout}>
            <div className="icon-box">
              <Logout size={20} variant="Bold" />
            </div>
            <div className="text-box">
              <h5>Sair</h5>
              <span>Encerrar sessão atual</span>
            </div>
            <ArrowRight2 size={18} className="arrow-icon" />
          </ActionListItem>
        </GroupCard>
      </Main>
    </MobileLayout>
  );
};

export default UserProfile;
